import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { MultiSelect } from 'primeng/multiselect';
import { ProgressSpinner } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs/operators';
import { DatabaseStringToHumanPipe } from '@workspace-app/shared';
import {
  RenewalTimelineService,
  TimelineFilter,
  TimelineItem,
  TimelineItemType,
} from '../../services/renewal-timeline.service';

@Component({
  selector: 'app-renewal-timeline-dialog',
  standalone: true,
  imports: [
    FormsModule,
    InputText,
    MultiSelect,
    ProgressSpinner,
    TooltipModule,
    FaIconComponent,
    DatabaseStringToHumanPipe,
  ],
  templateUrl: './renewal-timeline-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./renewal-timeline-dialog.component.scss'],
})
export class RenewalTimelineDialogComponent implements OnInit {
  private timelineService = inject(RenewalTimelineService);
  private config = inject(DynamicDialogConfig);

  reviewId: string;

  // Signals for state management
  loading = signal(false);
  timelineItems = signal<TimelineItem[]>([]);
  filteredItems = computed(() => {
    const items = this.timelineItems();
    const filter = this.activeFilter();

    if (!filter || Object.keys(filter).length === 0) {
      return items;
    }

    return this.timelineService.filterTimelineItems(items, filter);
  });

  activeFilter = signal<TimelineFilter>({});
  searchText = signal('');
  selectedTypes = signal<TimelineItemType[]>([]);

  // Filter options for PrimeNG multiselect
  typeOptionsForMultiselect = [
    { label: 'State Changes', value: 'state_change' },
    { label: 'Selection', value: 'selection' },
    { label: 'Certificates', value: 'certificate' },
    { label: 'Completion', value: 'completion' },
    { label: 'Cancellation', value: 'cancellation' },
    { label: 'Notes', value: 'note' },
    { label: 'Creation', value: 'creation' },
  ];
  constructor() {
    this.reviewId = this.config.data?.reviewId;

    // Update filter when search text or selected types change
    effect(() => {
      const filter: TimelineFilter = {};

      const search = this.searchText();
      if (search) {
        filter.searchText = search;
      }

      const types = this.selectedTypes();
      if (types.length > 0) {
        filter.types = types;
      }

      this.activeFilter.set(filter);
    });
  }

  ngOnInit() {
    if (this.reviewId) {
      this.loadTimelineItems();
    }
  }

  private loadTimelineItems() {
    this.loading.set(true);

    this.timelineService
      .getTimelineItems(this.reviewId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (items) => {
          this.timelineItems.set(items);
        },
        error: (error) => {
          console.error('Failed to load timeline items:', error);
        },
      });
  }

  refresh() {
    this.loadTimelineItems();
  }

  clearFilters() {
    this.searchText.set('');
    this.selectedTypes.set([]);
  }

  getItemClass(item: TimelineItem): string {
    return `timeline-item-${item.type}`;
  }

  formatDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
}
